import cn from "classnames";
import { Form, FormikProvider, useFormik } from "formik";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { PolygonColors, UserPolices } from "constant";
import features from "features";
import { calculateArea } from "helpers/calculateArea";
import usePolicy from "hooks/usePolicy";
import { RootStateInterface } from "reducer";
import usePolygonHandler from "scenes/MapPage/usePolygonHandler";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import IconButton from "components/buttons/IconButton/IconButton";
import LightButton from "components/buttons/LightButton/LightButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import FormField from "components/inputs/FormField/FormField";
import ColorConfigurator from "./ColorConfigurator";

import CloseSmallIcon from "assets/icons/close-small.svg";
import { ReactComponent as AreaIcon } from "assets/icons/square-area-black.svg";
import DeleteIcon from "assets/icons/trash-2.svg";

import styles from "./edit-polygon-form.module.scss";

interface EditPolygonFormInterface {
  areaData: any;
  onClose?: () => void;
}

enum ColorsSubjects {
  STROKE_COLOR = "stroke_color",
  FILL_COLOR = "fill_color"
}

enum OpacitySubjects {
  STROKE_TRANSPARENCY = "stroke_transparency",
  FILL_TRANSPARENCY = "fill_transparency"
}

interface ColorsInterface {
  colorsSubject: ColorsSubjects;
  opacitySubject: OpacitySubjects;
}

interface AreaColorInterface {
  colorName: string;
  color: string;
  opacity?: number;
  isActive: boolean;
  action: () => void;
}

const EditPolygonForm = ({ areaData, onClose }: EditPolygonFormInterface) => {
  const dispatch = useDispatch();
  const { onPolygonDelete, createPolygon } = usePolygonHandler(areaData);
  const { hasPolicy } = usePolicy();

  const { creationPolygonParameters } = useSelector<
    RootStateInterface,
    UnitsManagerStateInterface
  >((state) => state.unitsManager);

  const isExistingPolygon = !!areaData.id;

  const areaDataCalculated = calculateArea(areaData?.geo_data?.coordinates);

  const editPolygonValidationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required")
  });

  const onEditPolygonClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      dispatch(features.unitsManager.actions.unfreezeHistory());
      dispatch(features.modal.actions.hideModal());
    }
  }, [dispatch, onClose]);

  const formik = useFormik({
    validateOnChange: true,
    initialValues: areaData,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: editPolygonValidationSchema,
    onSubmit: createPolygon
  });

  const colorsAreaList = useCallback(
    ({
      colorsSubject,
      opacitySubject
    }: ColorsInterface): Array<AreaColorInterface> => {
      const onChangeColor = (subject: string, color: PolygonColors) => {
        formik.setFieldValue(`additional.${subject}`, color);
        dispatch(
          features.unitsManager.actions.updateCreationPolygonParameters({
            ...creationPolygonParameters,
            additional: {
              ...creationPolygonParameters.additional,
              [subject]: color
            }
          })
        );
      };

      return [
        {
          colorName: "red",
          color: PolygonColors.RED,
          opacity: formik.values.additional[`${opacitySubject}`],
          isActive:
            formik.values.additional[`${colorsSubject}`] === PolygonColors.RED,
          action: () => onChangeColor(colorsSubject, PolygonColors.RED)
        },
        {
          colorName: "orange",
          color: PolygonColors.ORANGE,
          opacity: formik.values.additional[`${opacitySubject}`],
          isActive:
            formik.values.additional[`${colorsSubject}`] ===
            PolygonColors.ORANGE,
          action: () => onChangeColor(colorsSubject, PolygonColors.ORANGE)
        },
        {
          colorName: "green",
          color: PolygonColors.GREEN,
          opacity: formik.values.additional[`${opacitySubject}`],
          isActive:
            formik.values.additional[`${colorsSubject}`] ===
            PolygonColors.GREEN,
          action: () => onChangeColor(colorsSubject, PolygonColors.GREEN)
        },
        {
          colorName: "yellow",
          color: PolygonColors.YELLOW,
          opacity: formik.values.additional[`${opacitySubject}`],
          isActive:
            formik.values.additional[`${colorsSubject}`] ===
            PolygonColors.YELLOW,
          action: () => onChangeColor(colorsSubject, PolygonColors.YELLOW)
        },
        {
          colorName: "blue",
          color: PolygonColors.BLUE,
          opacity: formik.values.additional[`${opacitySubject}`],
          isActive:
            formik.values.additional[`${colorsSubject}`] === PolygonColors.BLUE,
          action: () => onChangeColor(colorsSubject, PolygonColors.BLUE)
        }
      ];
    },
    [dispatch, creationPolygonParameters, formik]
  );

  const handlePolygonNameChange = useCallback(
    (e) => {
      formik.setFieldValue("name", e.target.value);
      dispatch(
        features.unitsManager.actions.updateCreationPolygonParameters({
          ...creationPolygonParameters,
          name: e.target.value
        })
      );
    },
    [dispatch, creationPolygonParameters, formik]
  );

  return (
    <FormikProvider value={formik}>
      <Form
        className={cn(styles["edit-polygon-wrapper"], {
          [styles["existing-polygon-wrapper"]]: isExistingPolygon
        })}
      >
        <div className={styles["edit-polygon__text-container"]}>
          <h2 className={styles["edit-polygon__title"]}>Customize area</h2>
        </div>
        <IconButton
          icon={CloseSmallIcon}
          className={cn(styles["edit-polygon__close-btn"])}
          alt="close"
          onClick={onEditPolygonClose}
        />
        <div className={styles["main-area-data-container"]}>
          <div className={styles["name-container"]}>
            <label className={styles["area-label"]}>Name</label>
            <FormField
              name={"name"}
              value={formik.values["name"]}
              placeholder="Name point"
              onChange={handlePolygonNameChange}
            />
          </div>
          <div className={styles["area-container"]}>
            <label className={styles["area-label"]}>Area</label>
            <div className={styles["area__count-container"]}>
              <AreaIcon />
              <span className={styles["area-count"]}>
                {areaDataCalculated.areaInHectares > 1
                  ? `${areaDataCalculated.areaInHectares.toFixed(5)} га`
                  : `${areaDataCalculated.areaInSquareMeters.toFixed(2)} м²`}
              </span>
            </div>
          </div>
        </div>
        <div className={styles["color-configuration-block"]}>
          <ColorConfigurator
            colorSubjectName="Stroke"
            colorsList={colorsAreaList({
              colorsSubject: ColorsSubjects.STROKE_COLOR,
              opacitySubject: OpacitySubjects.STROKE_TRANSPARENCY
            })}
            formValues={formik.values}
            setValue={formik.setFieldValue}
            opacitySubject={OpacitySubjects.STROKE_TRANSPARENCY}
            isExistingPolygon
          />
          <ColorConfigurator
            colorSubjectName="Fill"
            colorsList={colorsAreaList({
              colorsSubject: ColorsSubjects.FILL_COLOR,
              opacitySubject: OpacitySubjects.FILL_TRANSPARENCY
            })}
            formValues={formik.values}
            setValue={formik.setFieldValue}
            opacitySubject={OpacitySubjects.FILL_TRANSPARENCY}
            isExistingPolygon
          />
        </div>
        <div className={styles["edit-polygon__controls-wrapper"]}>
          {isExistingPolygon &&
            hasPolicy(UserPolices.PROJECT_PROVIDER_UNIT_DELETE) && (
              <CommonButton
                icon={DeleteIcon}
                onClick={onPolygonDelete}
                className={styles["delete-polygon-button"]}
              >
                Delete
              </CommonButton>
            )}
          <div className={styles["edit-polygon__form-controls-wrapper"]}>
            <LightButton textSize={14} onClick={onEditPolygonClose}>
              Cancel
            </LightButton>
            <PrimaryButton type="submit" textSize={14}>
              Save
            </PrimaryButton>
          </div>
        </div>
      </Form>
    </FormikProvider>
  );
};

export default EditPolygonForm;
